import React, { useContext, useEffect, useMemo } from 'react';
import { LanguageContext } from '../context/LanguageContext';
import CarouselImg from '../ui/CarouselImg';
import {
  Section,
  SectionTitle,
  FeatureCard,
  ResearchCard,
  SponsorCard,
  ContentBlock,
  CTAButton,
  TeamCarousel
} from '../ui/Home_UI';

// ==================== DATA FUNCTIONS ====================
const getTeamMembers = (t) => [
  {
    name: "Patrick Vandewalle",
    role: "KU Leuven",
    description: t.teamPositions["Patrick Vandewalle"],
    img: "/list_colab/colab_1.png",
    linkedIn: "https://www.linkedin.com/in/vandewallepatrick/"
  },
  {
    name: "Esteban Valencia",
    role: "EPN",
    description: t.teamPositions["Esteban Valencia"],
    img: "/list_colab/colab_2.png",
    linkedIn: "https://www.linkedin.com/in/esteban-valencia-torres/"
  },
  {
    name: "Jackeline Abad",
    role: "EPN",
    description: t.teamPositions["Jackeline Abad"],
    img: "/list_colab/colab_4.png",
    linkedIn: "https://www.linkedin.com/in/jackeline-abad-torres-a5090b29/"
  },
  {
    name: "Jenny Ávila",
    role: "EPN",
    description: t.teamPositions["Jenny Ávila"],
    img: "/list_colab/colab_12.jpg",
    linkedIn: "https://www.linkedin.com/in/jenny-%C3%A1vila-v%C3%A9lez-801a3443/"
  },
  {
    name: "Cristina Romero",
    role: "EPN",
    description: t.teamPositions["Cristina Romero"],
    img: "/list_colab/colab_8.png",
    linkedIn: "https://www.linkedin.com/in/cristina-romero-65450624/"
  },
];

// Organizados por categorías
const UNIVERSITIES = [
  { name: "Escuela Politécnica Nacional", img: "/list_colab/EPN_logo_big.png" },
  { name: "Katholieke Universiteit Leuven", img: "/list_colab/colab_10.png" }
];

const RESEARCH_GROUPS = [
  { name: "ATA Research Group", img: "/list_colab/logo_h_ ATA.png" },
  { name: "Embedded and Artificially intelligent VISion Engineering", img: "/list_colab/eavise_logo.png" }
];

const COLLABORATING_ENTITIES = [
  { name: "Delgado Pitahaya", img: "/images/pitahaya_logo.png" },
  { name: "Instituto Nacional de Investigación Agropecuarias", img: "/list_colab/colab_11.png" },
  { name: "CAVOUND FairFood", img: "/list_colab/cavound.png" },
  { name: "AgroCalidad", img: "/list_colab/agrocalidad.png" },
  { name: "Embajada de Francia en Ecuador", img: "/images/france_embassy.png" },
  { name: "Embajada de Bélgica en Ecuador", img: "/images/belgic_embassy.png" }
 
];

const PROJECT_FEATURES = [
  { icon: "fa-solid fa-desktop", key: "projectsubDescription1" },
  { icon: "fa-solid fa-robot", key: "projectsubDescription2" },
  { icon: "fa-solid fa-chart-line", key: "projectsubDescription3" }
];

const RESEARCH_LINES = [
  { icon: "fa-solid fa-seedling", key: "newLinesDescription1" },
  { icon: "fa-solid fa-brain", key: "newLinesDescription2" },
  { icon: "fa-solid fa-droplet", key: "newLinesDescription3" },
  { icon: "fa-solid fa-building-wheat", key: "newLinesDescription4" }
];

// ==================== CUSTOM HOOKS ====================
const useElfsightWidget = () => {
  useEffect(() => {
    if (document.querySelector('script[src*="elfsight.com"]')) return;
    
    const script = document.createElement('script');
    script.src = "https://static.elfsight.com/platform/platform.js";
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      try {
        if (script.parentNode) document.body.removeChild(script);
      } catch (e) {
        console.warn('Script cleanup:', e.message);
      }
    };
  }, []);
};

const useCarouselData = (t) => useMemo(() => [
  {
    src: "/images/team_drone.jpg",
    alt: "Imagen principal 1",
    title: t.mainTitle || "Sembrando tecnología, cosechando Futuro",
    description: t.mainSubTitle || "Agricultura inteligente para comunidades resilientes",
  },
  {
    src: "/images/gallery_1.jpg",
    alt: "Imagen principal 2",
    title: t.mainTitle2 || "Innovar para cultivar con conciencia",
    description: t.mainSubTitle2 || "Sostenibilidad y tecnología al servicio del agricultor",
  },
  {
    src: "/images/monitoreo.jpeg",
    alt: "Monitoreo Inteligente",
    title: t.mainTitle3 || "Más allá del Cultivo",
    description: t.mainSubTitle3 || "Cómo la pitahaya impulsa inclusión, ingresos y esperanza",
  },
], [t]);

// ==================== PAGE SECTIONS ====================
const HeroCarousel = ({ images }) => (
  <section className="relative w-full">
    <CarouselImg images={images} />
  </section>
);

const ProjectSection = ({ t }) => (
  <Section className="bg-gray-50">
    <SectionTitle subtitle={t.projectDescription1}>
      {t.projectTitle || "Nuestro Proyecto"}
    </SectionTitle>
    
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
      {PROJECT_FEATURES.map((feature, idx) => (
        <FeatureCard key={idx} icon={feature.icon} text={t[feature.key]} />
      ))}
    </div>
    
    <p className="text-xl text-black text-center max-w-4xl mx-auto leading-relaxed">
      {t.projectDescription2}
    </p>
  </Section>
);

const AboutSection = ({ t }) => (
  <Section id="sobre-el-proyecto" className="bg-white">
    <SectionTitle>{t.aboutProject}</SectionTitle>

    <div className="space-y-28">
      <ContentBlock
        title={t.newThemeTitle}
        description={<p className="text-justify">{t.newThemeDescription}</p>}
        imageSrc="/images/postal.jpeg"
        imageAlt="Objetivos del Proyecto"
        action={<CTAButton to="/que-hacemos">¿{t.buttonText2}?</CTAButton>}
      />

      <ContentBlock
        title={t.newLines}
        description={
          <div className="space-y-4">
            {RESEARCH_LINES.map((line, idx) => (
              <ResearchCard key={idx} icon={line.icon} text={t[line.key]} />
            ))}
          </div>
        }
        imageSrc="/images/drone_1.jpg"
        imageAlt="Líneas de Investigación"
        reverse
      />
    </div>
  </Section>
);

const SponsorSection = ({ t }) => (
  <Section className="bg-gradient-to-br from-emerald-900 to-teal-900 text-white">
    <ContentBlock
      title={<span className="text-[#05ac5b]">{t.newSponsorTitle}</span>}
      description={<p className="text-justify text-black text-lg">{t.newSponsorDescription}</p>}
      imageSrc="/images/vliruos.png"
      imageAlt="VLIRUOS Logo"
    />
  </Section>
);

// Sección organizada por categorías
const ParticipantsSection = ({ t }) => (
  <Section className="bg-gray-50">
    <div className="space-y-16">
      {/* Universidades Participantes */}
      <div>
        <h3 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {t.universitiesTitle || "Universidades Participantes"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {UNIVERSITIES.map((university, idx) => (
            <SponsorCard key={idx} {...university} />
          ))}
        </div>
      </div>

      {/* Grupos de Investigación */}
      <div>
        <h3 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {t.researchGroupsTitle || "Grupos de Investigación"}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {RESEARCH_GROUPS.map((group, idx) => (
            <SponsorCard key={idx} {...group} />
          ))}
        </div>
      </div>

      {/* Entidades Colaboradoras */}
      <div>
        <h3 className="text-3xl font-bold text-center text-gray-800 mb-8">
          {t.collaboratingEntitiesTitle || "Entidades Colaboradoras"}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8">
          {COLLABORATING_ENTITIES.map((entity, idx) => (
            <SponsorCard key={idx} {...entity} />
          ))}
        </div>
      </div>
    </div>
  </Section>
);

const TeamSection = ({ t, members }) => (
  <Section className="bg-white">
    <SectionTitle>{t.teamTitle}</SectionTitle>
    <TeamCarousel members={members} t={t} />
  </Section>
);

// ==================== MAIN COMPONENT ====================
const Home = () => {
  const { language, translations } = useContext(LanguageContext);
  const t = translations[language];
  const carouselImages = useCarouselData(t);
  const teamMembers = getTeamMembers(t);
  
  useElfsightWidget();

  return (
    <div className="min-h-screen bg-[#f9f9f9]">
      <HeroCarousel images={carouselImages} />
      <ProjectSection t={t} />
      <AboutSection t={t} />
      <SponsorSection t={t} />
      <ParticipantsSection t={t} />
      <TeamSection t={t} members={teamMembers} />

      <Section className="bg-gray-50">
        <div className="elfsight-app-29264217-0f61-4414-a027-48359c08ed39" data-elfsight-app-lazy />
      </Section>
    </div>
  );
};

export default Home;